/**
 * Calculate the number of days between two dates.
 * Returns 1 if dates are missing or same-day.
 */
export const calculateBookingDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 1
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end - start)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays || 1
}

/**
 * Calculate the total booking price based on service pricing model and booking details.
 */
export const calculateBookingPrice = (service, bookingData) => {
  if (!service) return 0

  let price = Number.parseFloat(service.base_price)
  const days = calculateBookingDays(bookingData.start_date, bookingData.end_date)
  const quantity = Number.parseInt(bookingData.quantity, 10) || 1

  if (service.price_unit === 'per_day') {
    price *= Math.max(1, days)
  } else if (service.price_unit === 'per_person') {
    const numAdults = Number.parseInt(bookingData.num_adults, 10) || 0
    const numChildren = Number.parseInt(bookingData.num_children, 10) || 0
    price *= numAdults + numChildren
    if (service.type === 'activity' && days > 1) {
      price *= days
    }
  }

  return price * quantity
}

/**
 * Build a price breakup snapshot for a tour booking.
 * @param {object} params
 * @param {object} params.tour           - Tour object { title, base_price }
 * @param {number} params.numAdults
 * @param {number} params.numChildren
 * @param {object[]} params.services     - Flat array of selected services (with quantity, price_unit, base_price, name)
 * @param {Function} params.calcServiceCost - fn(service) => number
 * @param {object|null} params.discount  - appliedDiscount object or null
 * @param {number} params.subtotal       - Pre-discount total
 * @param {number} params.total          - Final price after discount
 * @returns {object} price breakup JSON
 */
export const buildPriceBreakup = ({
  tour,
  numAdults,
  numChildren,
  services = [],
  calcServiceCost,
  discount,
  subtotal,
  total,
}) => {
  const basePrice = Number.parseFloat(tour.base_price) || 0
  const adultTotal = basePrice * numAdults
  const childTotal = basePrice * 0.5 * numChildren

  const breakup = {
    tour: {
      name: tour.title || tour.name,
      base_price: basePrice,
      adults: numAdults,
      children: numChildren,
      adult_total: adultTotal,
      child_total: childTotal,
    },
    services: services.map((s) => ({
      name: s.name,
      type: s.type || null,
      base_price: Number.parseFloat(s.base_price) || 0,
      quantity: s.quantity || 1,
      price_unit: s.price_unit || 'flat',
      cost: calcServiceCost(s),
    })),
    subtotal,
    total,
  }

  if (discount) {
    breakup.discount = {
      code: discount.code || discount.discount_code || null,
      type: discount.discount_type,
      value: Number.parseFloat(discount.discount_value),
      amount: Number.parseFloat(discount.discount_amount),
    }
  }

  return breakup
}
